import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Users, Network, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { clientGroupsAPI } from '../lib/clientGroupsAPI';

const groupTypeColor = (t) => ({
  family: 'bg-purple-100 text-purple-800',
  business: 'bg-blue-100 text-blue-800',
  mixed: 'bg-amber-100 text-amber-800',
}[t] || 'bg-muted text-foreground');

export default function ClientGroups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [userRole, setUserRole] = useState(undefined);

  const canManageFirm = ['owner', 'admin'].includes(userRole);

  useEffect(() => {
    api.get('/api/me').then((res) => setUserRole(res.data.active_membership?.role)).catch(() => {});
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const res = await clientGroupsAPI.list();
      setGroups(res.data.groups || []);
    } catch (err) {
      toast.error('Failed to load client groups');
    } finally {
      setLoading(false);
    }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await clientGroupsAPI.backfill();
      toast.success(`Created ${res.data.backfilled} group(s) for existing clients`);
      loadGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="client-groups-title">
            Client Groups
          </h1>
          <p className="text-muted-foreground mt-1">
            Family &amp; entity groups link related clients — spouse, companies, trusts and SMSFs — under one primary client.
          </p>
        </div>
        {canManageFirm && (
          <Button variant="outline" onClick={handleBackfill} disabled={backfilling} data-testid="backfill-groups-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${backfilling ? 'animate-spin' : ''}`} />
            Backfill Existing Clients
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="animate-pulse h-64 bg-muted rounded" />
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <Network className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No client groups yet.</p>
              <p className="text-muted-foreground/70 text-sm mt-1">
                Groups are created automatically when a client is added.
                {canManageFirm && ' Use “Backfill Existing Clients” to group clients created earlier.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Group Name</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                    <TableHead className="font-semibold">Primary Client</TableHead>
                    <TableHead className="font-semibold">Members</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => (
                    <TableRow
                      key={g.id}
                      className="hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/client-groups/${g.id}`)}
                      data-testid={`group-row-${g.id}`}
                    >
                      <TableCell className="font-medium text-foreground">{g.group_name}</TableCell>
                      <TableCell><Badge className={groupTypeColor(g.group_type)}>{g.group_type}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{g.primary_client_name || '—'}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 text-sm text-foreground/90">
                          <Users className="w-4 h-4 text-muted-foreground/70" />
                          {g.member_count}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
